import React, { useState } from 'react';
import {
  GraduationCap,
  BookOpen,
  Video,
  BarChart3,
  ArrowRight,
  Award,
  Trophy,
  ShieldCheck,
  CheckCircle2,
  Terminal,
  Activity,
  PlayCircle,
  FileCheck2,
  Users,
  Brain,
  ChevronLeft,
  ChevronRight,
  Play,
  Monitor,
  Check,
  Search,
  Compass,
  Layers,
  Palette,
  Network,
  Rocket,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LMSSolution = () => {
  const [activePlatform, setActivePlatform] = useState('corporate');
  const [activeStep, setActiveStep] = useState(0);
  const [activePreviewTab, setActivePreviewTab] = useState('student');

  const lmsPillars = [
    {
      icon: BookOpen,
      title: 'Course Builder & SCORM / xAPI Engine',
      badge: 'SCORM 1.2 / xAPI',
      color: 'from-cyan-500 to-blue-600',
      description: 'Drag-and-drop course creation supporting video lectures, PDF workbooks, interactive HTML5 SCORM packages, and modular lesson branching.',
      bullets: ['SCORM 1.2 & xAPI Package Import', 'Drag-and-Drop Curriculum Builder', 'Modular Lesson Branching Rules']
    },
    {
      icon: Video,
      title: 'HD Live Classroom & Video Streaming',
      badge: 'WebRTC & HLS',
      color: 'from-purple-500 to-indigo-600',
      description: 'Adaptive HLS video streaming, integrated live WebRTC video webinars, interactive Q&A chat, and automated session recording archives.',
      bullets: ['Adaptive Bitrate Video Streaming', 'Live WebRTC Video Classrooms', 'Automated Webinar Recording Archive']
    },
    {
      icon: Brain,
      title: 'AI Adaptive Learning & AI Tutor',
      badge: 'AI Powered',
      color: 'from-orange-500 to-amber-500',
      description: 'Personalized learning path recommendations, AI study assistant chat, automated quiz question generation, and adaptive difficulty scaling.',
      bullets: ['Personalized Study Path Recommendations', '24/7 AI Study Assistant Copilot', 'Automated Quiz Generation Engine']
    },
    {
      icon: Award,
      title: 'Automated Quizzes & Certifications',
      badge: 'Instant Certificate',
      color: 'from-emerald-500 to-teal-600',
      description: 'Automated quiz grading, webcam anti-cheat proctoring checks, passing grade thresholds, and instant PDF certificate distribution.',
      bullets: ['Automated Quiz & Assignment Grading', 'Webcam Anti-Cheat Proctoring', 'Instant PDF & Blockchain Certificates']
    },
    {
      icon: Users,
      title: 'Corporate Compliance & Skill Tracking',
      badge: 'Team Dashboards',
      color: 'from-pink-500 to-rose-600',
      description: 'Mandatory annual compliance tracking, automated recertification alerts, manager team oversight, and employee skill matrix reports.',
      bullets: ['Mandatory Compliance Tracking', 'Automated Recertification Alerts', 'Manager Team Progress Telemetry']
    },
    {
      icon: Trophy,
      title: 'Gamification & Social Learning',
      badge: 'XP & Badges',
      color: 'from-blue-500 to-cyan-600',
      description: 'XP point rewards, achievement badges, streak counters, course discussion forums, and competitive organization leaderboards.',
      bullets: ['Achievement Badges & XP Rewards', 'Student Discussion Forums', 'Organization Leaderboard Ranking']
    }
  ];

  const platformSpecs = {
    corporate: {
      name: 'Enterprise Corporate Training Portal',
      desc: 'Onboard new hires 3x faster, automate annual compliance certifications, and track employee skill matrices across global teams.',
      specs: ['Automated New Hire Onboarding Tracks', 'Annual Security & HR Compliance Sync', 'Manager Department Dashboards', 'Single Sign-On (SSO) & SAML Integration']
    },
    university: {
      name: 'University & Academic Higher Ed LMS',
      desc: 'Power virtual campuses with live lecture streaming, gradebook management, assignment submission turn-in, and plagiarism detection.',
      specs: ['Faculty Gradebook & Marking Matrix', 'Turnitin Plagiarism Detection Integration', 'Student Assignment Portal & Peer Review', 'Semester & Course Registration Engine']
    },
    edtech: {
      name: 'EdTech Course Marketplace Platform',
      desc: 'Monetize digital courses with subscription paywalls, Stripe / PayPal checkout, affiliate referral tools, and mobile learning apps.',
      specs: ['Stripe & PayPal One-Time / Recurring Paywall', 'Instructor Revenue Share & Payouts', 'Affiliate Referral & Coupon Engine', 'iOS & Android Native Mobile App Sync']
    }
  };

  const lmsImplementationRoadmap = [
    {
      step: '01',
      title: 'Discovery & Learning Strategy',
      desc: 'Analyze organizational goals, training requirements, learner personas, compliance needs, and define the LMS implementation roadmap.',
      icon: Search,
      badge: 'Strategy'
    },
    {
      step: '02',
      title: 'Solution Planning & System Architecture',
      desc: 'Design the LMS architecture, user roles, learning paths, permissions, branding, and platform configuration.',
      icon: Layers,
      badge: 'Architecture'
    },
    {
      step: '03',
      title: 'UI/UX Design & Platform Customization',
      desc: 'Create an intuitive, branded learning experience with customized dashboards, portals, and responsive interfaces.',
      icon: Palette,
      badge: 'UI/UX Design'
    },
    {
      step: '04',
      title: 'Course Development & Content Migration',
      desc: 'Develop or migrate SCORM, xAPI, videos, documents, quizzes, assessments, and certifications into the LMS.',
      icon: Video,
      badge: 'Content & SCORM'
    },
    {
      step: '05',
      title: 'Integration & Automation',
      desc: 'Integrate HRMS, ERP, CRM, Single Sign-On (SSO), payment gateways, video conferencing, email notifications, and third-party tools to streamline workflows.',
      icon: Network,
      badge: 'Integrations'
    },
    {
      step: '06',
      title: 'Testing & Quality Assurance',
      desc: 'Conduct functional, security, compatibility, accessibility, and performance testing to ensure a reliable learning experience.',
      icon: ShieldCheck,
      badge: 'QA & Security'
    },
    {
      step: '07',
      title: 'Deployment & User Onboarding',
      desc: 'Launch the LMS, configure production environments, onboard administrators, instructors, and learners, and provide training.',
      icon: Rocket,
      badge: 'Onboarding'
    },
    {
      step: '08',
      title: 'Analytics, Support & Continuous Improvement',
      desc: 'Monitor learner engagement, generate reports, optimize platform performance, implement feature enhancements, and provide ongoing maintenance and technical support.',
      icon: BarChart3,
      badge: 'Optimization'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-20 text-gray-100">

      {/* Hero Showcase Section with Dark LMS Technology Background Banner */}
      <section className="py-24 relative overflow-hidden border-b border-gray-800/80 bg-gray-950">
        {/* Background Image Layer */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/dark_lms_hero_bg.png"
            alt="LMS Digital Education Background Banner"
            className="w-full h-full object-cover filter brightness-90 contrast-110 opacity-75 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/85 to-gray-950/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-gray-950/80" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 mb-6">
                <GraduationCap className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider">
                  Next-Gen EdTech & LMS Engine v3.0
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                Smart Enterprise <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">Learning Management System</span>
              </h1>

              <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8">
                Deliver interactive online courses, live video classrooms, automated quiz evaluations, SCORM compliance, and AI-driven personalized learning paths for corporate training and universities.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                    Request LMS Demo
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#lms-preview">
                  <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                    Preview Student Portal
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Live LMS Telemetry Console */}
            <div className="lg:col-span-5">
              <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-6 shadow-2xl relative overflow-hidden backdrop-blur-xl">

                {/* Console Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-800 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-purple-400 animate-ping" />
                    <span className="text-sm font-mono text-purple-400 font-bold">LMS STREAMING: ONLINE</span>
                  </div>
                  <span className="text-[11px] font-mono text-gray-400">SCORM v1.2 / HLS</span>
                </div>

                {/* Telemetry Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Active Students</div>
                    <div className="text-2xl font-extrabold text-cyan-400 font-mono">84,200</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Completion Rate</div>
                    <div className="text-2xl font-extrabold text-orange-400 font-mono">89.4%</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Video Stream</div>
                    <div className="text-2xl font-extrabold text-emerald-400 font-mono">&lt; 180ms</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">AI Tutor</div>
                    <div className="text-2xl font-extrabold text-blue-400 font-mono">ACTIVE</div>
                  </div>
                </div>

                {/* Terminal Line */}
                <div className="bg-gray-950 p-3 rounded-xl border border-gray-800 font-mono text-sm text-gray-400 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-purple-400" />
                  <span>lms_engine.issue_completion_certificate(#USER-9204)</span>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Performance Metrics Banner */}
      <section className="py-10 bg-white border-y border-gray-800/80">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-cyan-400 mb-1">89.4%</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Course Completion Rate</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-orange-400 mb-1">84,000+</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Active Students & Trainees</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-blue-400 mb-1">SCORM</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">1.2 & xAPI Certified</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-emerald-400 mb-1">4.9 ★</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Learner Experience Score</div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive LMS Student & Instructor Portal Preview Showcase */}
      <section id="lms-preview" className="py-24 bg-gray-900/60 border-b border-gray-800/80">
        <div className="container mx-auto px-6">

          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-block bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider">
                Live Interactive Platform Concept
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Interactive <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">LMS Campus Experience</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Explore how students, instructors, and corporate admins interact with our real-time learning portal interface.
            </p>

            <div className="inline-flex p-1 bg-gray-950 border border-gray-800 rounded-xl space-x-2 mt-6">
              <button
                onClick={() => setActivePreviewTab('student')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activePreviewTab === 'student'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Student Dashboard & AI Tutor
              </button>
              <button
                onClick={() => setActivePreviewTab('video')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activePreviewTab === 'video'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                HD Live Classroom & WebRTC
              </button>
              <button
                onClick={() => setActivePreviewTab('gradebook')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activePreviewTab === 'gradebook'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Instructor Gradebook & Proctoring
              </button>
            </div>
          </div>

          {/* Interactive Portal Preview Mockup Window */}
          <div className="bg-gray-950 border border-gray-800 rounded-3xl p-6 md:p-10 shadow-2xl relative overflow-hidden backdrop-blur-xl max-w-5xl mx-auto">

            {/* Window Top Controls */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-800 mb-8">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-rose-500" />
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-sm font-mono text-gray-400 ml-2">https://campus.hoasp.com/portal</span>
              </div>
              <div className="text-sm font-mono text-purple-400 font-bold uppercase">LIVE CAMPUS UI SIMULATION</div>
            </div>

            {/* TAB 1: Student Dashboard & AI Tutor */}
            {activePreviewTab === 'student' && (
              <div className="grid md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-7 space-y-4">
                  <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/30 px-3 py-1 rounded-full text-sm text-cyan-300">
                    <BookOpen className="w-3.5 h-3.5 text-cyan-400" />
                    <span>Active Course: Full-Stack React & Node.js</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white">Lesson 14: Micro-Service Architecture & API Integration</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Track your module progress, download exercise workbooks, and ask the integrated 24/7 AI Tutor copilot clarifying questions.
                  </p>

                  <div className="space-y-2 pt-2">
                    <div className="flex justify-between text-sm text-gray-400 font-mono">
                      <span>COURSE COMPLETION</span>
                      <span className="text-cyan-400 font-bold">78% COMPLETED</span>
                    </div>
                    <div className="w-full bg-gray-900 h-2 rounded-full overflow-hidden border border-gray-800">
                      <div className="bg-gradient-to-r from-cyan-400 to-blue-500 h-full w-[78%]" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="bg-gray-900 border border-gray-800 p-3 rounded-xl flex items-center space-x-2">
                      <Trophy className="w-4 h-4 text-orange-400" />
                      <span className="text-sm text-gray-200 font-semibold">1,450 XP Earned</span>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 p-3 rounded-xl flex items-center space-x-2">
                      <Award className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-gray-200 font-semibold">Quiz Master Badge</span>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-5 bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4">
                  <div className="flex items-center justify-between border-b border-gray-800 pb-3">
                    <div className="flex items-center space-x-2">
                      <Brain className="w-4 h-4 text-purple-400" />
                      <span className="text-sm font-bold text-white">AI Study Assistant Copilot</span>
                    </div>
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="bg-gray-950 p-3 rounded-xl text-gray-300">
                      <span className="text-cyan-400 font-bold">Student:</span> What is the difference between REST and GraphQL in Unit 4?
                    </div>
                    <div className="bg-purple-950/40 border border-purple-500/30 p-3 rounded-xl text-gray-200">
                      <span className="text-purple-400 font-bold">AI Tutor:</span> REST uses multiple endpoints per entity, while GraphQL uses a single endpoint where clients request exact fields!
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: HD Live Classroom & WebRTC */}
            {activePreviewTab === 'video' && (
              <div className="grid md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-8 relative h-72 rounded-2xl overflow-hidden border border-gray-800 group shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80"
                    alt="Live Video Classroom"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/30 to-transparent" />
                  <div className="absolute top-4 left-4 bg-red-600/90 text-white text-sm font-bold px-3 py-1 rounded-full flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-white animate-ping" />
                    <span>LIVE WEBRTC WEBINAR</span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 bg-gray-900/90 backdrop-blur-md p-3 rounded-xl border border-gray-800 flex items-center justify-between">
                    <span className="text-sm font-bold text-white">Lecture 8: Scalable Cloud Systems (Dr. Alan Vance)</span>
                    <span className="text-sm font-mono text-cyan-400 font-bold">284 ATTENDEES</span>
                  </div>
                </div>

                <div className="md:col-span-4 bg-gray-900 border border-gray-800 rounded-2xl p-4 space-y-3">
                  <div className="text-sm font-bold text-white border-b border-gray-800 pb-2">LIVE CLASSROOM CHAT</div>
                  <div className="space-y-2 text-[11px] text-gray-300">
                    <div className="bg-gray-950 p-2 rounded-lg">
                      <span className="text-orange-400 font-bold">Sarah M:</span> Will the recording be available under Unit 3?
                    </div>
                    <div className="bg-gray-950 p-2 rounded-lg">
                      <span className="text-cyan-400 font-bold">Prof Alan:</span> Yes! Automated HLS recording will process in 10 mins.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3: Instructor Gradebook & Proctoring */}
            {activePreviewTab === 'gradebook' && (
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-800 pb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">Faculty Gradebook & Proctoring Dashboard</h3>
                    <p className="text-sm text-gray-400">Class Batch: CS-2026 Advanced Software Engineering</p>
                  </div>
                  <div className="flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1.5 rounded-xl text-sm text-emerald-400">
                    <ShieldCheck className="w-4 h-4" />
                    <span>Proctoring Integrity: 100% Verified</span>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gray-900 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Average Exam Pass Rate</div>
                    <div className="text-2xl font-extrabold text-cyan-400 font-mono">92.4%</div>
                  </div>
                  <div className="bg-gray-900 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Certificates Issued</div>
                    <div className="text-2xl font-extrabold text-orange-400 font-mono">1,420 PDF</div>
                  </div>
                  <div className="bg-gray-900 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Cheating Alerts</div>
                    <div className="text-2xl font-extrabold text-emerald-400 font-mono">0 DETECTED</div>
                  </div>
                </div>
              </div>
            )}

          </div>

        </div>
      </section>

      {/* Pillars Grid */}
      <section id="lms-pillars" className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider">
                Full-Spectrum LMS Capabilities
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Enterprise LMS <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">Capabilities</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              From SCORM course builders to live WebRTC classrooms, AI study tutors, and anti-cheat proctoring.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {lmsPillars.map((pillar, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-purple-500/70 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${pillar.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <pillar.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full">
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors">
                    {pillar.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800/80 space-y-2">
                  {pillar.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive Platform Selector */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-4">
              Tailored for Every Education & Training Model
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-8">
              Modular learning architecture designed specifically for corporate enterprise training, universities, or commercial EdTech marketplaces.
            </p>

            <div className="inline-flex p-1 bg-gray-900 border border-gray-800 rounded-xl space-x-2">
              <button
                onClick={() => setActivePlatform('corporate')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activePlatform === 'corporate'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Corporate Training
              </button>
              <button
                onClick={() => setActivePlatform('university')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activePlatform === 'university'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                University Campus
              </button>
              <button
                onClick={() => setActivePlatform('edtech')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activePlatform === 'edtech'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                EdTech Marketplace
              </button>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-2xl text-left">
            <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">
              {platformSpecs[activePlatform].name}
            </h3>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              {platformSpecs[activePlatform].desc}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {platformSpecs[activePlatform].specs.map((spec, idx) => (
                <div key={idx} className="bg-slate-50 border border-gray-200 rounded-xl p-4 flex items-center space-x-3 shadow-xs">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-sm font-bold text-gray-900">{spec}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 8-Step Enterprise LMS Implementation Roadmap - White Background Theme */}
      <section id="lms-roadmap" className="py-24 bg-slate-50 border-t border-b border-gray-200 relative text-left">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-purple-100 border border-purple-300 text-purple-900 rounded-full px-4 py-1.5 mb-4 shadow-sm">
              <span className="text-xs font-extrabold uppercase tracking-wider">HOASP Implementation Methodology</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight leading-tight">
              8-Step Enterprise LMS Implementation Roadmap
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              From initial discovery and SCORM course migration to HRMS integration, user onboarding, and continuous analytics optimization.
            </p>
          </div>

          {/* 8-Step Card Grid with Enterprise White Styling */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lmsImplementationRoadmap.map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-200 rounded-3xl p-7 shadow-sm hover:shadow-2xl transition-all duration-300 relative flex flex-col justify-between group hover:border-purple-500/50 hover:-translate-y-1"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-2xl font-black text-purple-700 bg-purple-50 border border-purple-200 px-3 py-1 rounded-xl font-mono shadow-xs">
                      {item.step}
                    </span>
                    <div className="p-3 bg-slate-100 border border-slate-200 rounded-2xl text-slate-800 group-hover:bg-slate-900 group-hover:text-purple-400 transition-colors">
                      <item.icon className="w-5 h-5" />
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug group-hover:text-purple-700 transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-xs text-gray-600 leading-relaxed mb-6">
                    {item.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full">
                    {item.badge}
                  </span>
                  <span className="text-[11px] font-mono text-gray-400">PHASE {item.step}/08</span>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Full-Width High-Tech LMS Design Strip with Campus Image */}
      <section className="py-20 bg-gradient-to-r from-gray-950 via-slate-900 to-gray-950 relative overflow-hidden border-t border-purple-500/30 w-full shadow-2xl">
        <div className="container mx-auto px-6 relative z-10">
          <div className="bg-gradient-to-r from-slate-900/90 via-gray-900/90 to-slate-900/90 border border-gray-800 hover:border-purple-500/50 rounded-3xl p-8 md:p-12 shadow-2xl backdrop-blur-xl">
            <div className="grid lg:grid-cols-12 gap-8 items-center">

              {/* Left Column HD LMS Image Frame */}
              <div className="lg:col-span-5 relative h-64 md:h-72 rounded-2xl overflow-hidden border border-gray-800 shadow-2xl group">
                <img
                  src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80"
                  alt="LMS Digital Classroom & Education"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/20 to-transparent" />

                {/* Floating Image Label Badge */}
                <div className="absolute bottom-3 left-3 right-3 bg-gray-950/90 border border-purple-500/30 p-2.5 rounded-xl flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <GraduationCap className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-bold text-white">Smart EdTech & LMS Campus</span>
                  </div>
                  <span className="text-[11px] font-mono text-cyan-300 font-bold">SCORM COMPLIANT</span>
                </div>
              </div>

              {/* Right Column Content & Action Card */}
              <div className="lg:col-span-7 space-y-5 text-left">
                <div className="inline-flex items-center space-x-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-3.5 py-1">
                  <span className="text-purple-400 text-[11px] font-mono font-bold uppercase tracking-widest">
                    NEXT-GEN EDTECH & LMS ARCHITECTURE
                  </span>
                </div>

                <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">
                  Ready to Launch Your Custom <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">LMS Learning Portal?</span>
                </h2>

                <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                  Consult with our EdTech software engineers to build SCORM-compliant course players, live WebRTC video classrooms, AI study tutors, and mobile learning apps.
                </p>

                <div className="flex flex-wrap gap-3 pt-1">
                  <div className="flex items-center space-x-2 bg-gray-950 border border-gray-800 px-3.5 py-1.5 rounded-xl text-sm text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                    <span>SCORM 1.2 / xAPI Certified</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-950 border border-gray-800 px-3.5 py-1.5 rounded-xl text-sm text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-orange-400" />
                    <span>84,000+ Active Students</span>
                  </div>
                </div>

                <div className="pt-2">
                  <a href="/contact">
                    <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all flex items-center gap-2">
                      <GraduationCap className="w-5 h-5" />
                      Schedule Executive LMS Demo
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

    </div>
  );
};

export default LMSSolution;